import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Typography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import CloseIcon from 'components/material-symbol/icons/Close'
import EditOutlinedIcon from 'components/material-symbol/icons/Edit'
import NotificationsOffOutlinedIcon from 'components/material-symbol/icons/NotificationsOff'
import NotificationsActiveOutlinedIcon from 'components/material-symbol/icons/NotificationsActive'
import VisibilityOffOutlined from 'components/material-symbol/icons/VisibilityOff'
import VisibilityOutlined from 'components/material-symbol/icons/Visibility'
import MoreHorizIcon from 'components/material-symbol/icons/MoreHoriz'
import ActionsMenu from 'components/actions-menu'
import { useAuth } from 'lib/auth'
import { useStreamClient } from 'lib/stream-messaging/hooks'
import { useSnackbar } from 'notistack'
import { Fragment, useContext, useEffect, useReducer, useState } from 'react'
import { useChatContext } from 'stream-chat-react'
import { LeaveGroupConversation } from '../icons/leave-group'
import GroupAddOutlinedIcon from 'components/material-symbol/icons/GroupAdd'
import EditGroupNameModal from './edit-group-name-modal'
import AddMembersDialog from './add-members-dialog'
import MuteChannelDialog from './mute-channel-dialog'
import { isDistinctChannel, isGroupChannel } from '../utils'
import { ChannelListContext } from '../channel-list-context'
import { usePromotions } from '../promotions/promotions-context'
import VolumeUpIcon from 'components/material-symbol/icons/VolumeUp'

const useConfirmationDialogStyles = makeStyles(theme => ({
  dialogTitle: {
    margin: 0,
    padding: theme.spacing(3, 3, 2),
    position: 'relative',
  },
  title: {
    fontWeight: 600,
    fontSize: '1.125rem',
    lineHeight: 1.4,
    paddingRight: theme.spacing(5),
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1.5),
    top: theme.spacing(1.5),
    color: theme.palette.text.secondary,
  },
}))

const useStyles = makeStyles(() => ({
  overflowButton: {
    minWidth: 40,
    width: 40,
    height: 40,
    padding: 0,
    borderRadius: '50px',
    border: '1px solid #E0E0E0',
    backgroundColor: '#FFFFFF',
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#F5F5F5',
      borderColor: '#E0E0E0',
      boxShadow: 'none',
    },
  },
  menuIcon: {
    minWidth: 36,
    color: 'rgba(0, 0, 0, 0.54)',
  },
}))

const initialMenuState = {
  isConfirmationOpen: false,
  onSuccess: null,
  title: '',
  actionText: '',
  content: '',
}

function reducer(state, action) {
  switch (action.type) {
    case 'leave':
      return {
        isConfirmationOpen: true,
        onSuccess: action.onSuccess,
        successMessage: 'You have left the group',
        text: 'Leave Group',
        actionText: 'Leave Group',
        content:
          'Are you sure you want to leave? You will no longer be able to view or send messages to this group.',
      }
    case 'hide':
      return {
        isConfirmationOpen: true,
        onSuccess: action.onSuccess,
        successMessage: 'Conversation hidden',
        text: 'Hide Message',
        actionText: 'Hide',
        content:
          'Hiding this message will temporarily remove it from the list until you receive a new message or decide to unhide it.',
      }
    case 'close':
      return { ...initialMenuState }
    default:
      return state
  }
}

/**
 * Action menu for Stream conversation header.
 * Available actions: "Leave" and "Hide".
 * It automatically redirects to the next conversation on action completion.
 *
 * @param {Object} channel - the object of the current Stream channel
 */
function ConversationActionsMenu({ channel }) {
  const classes = useStyles()
  const { user } = useAuth()
  const [state, dispatch] = useReducer(reducer, initialMenuState)
  const client = useStreamClient()
  const { setActiveChannel } = useChatContext()
  const { setListTab } = useContext(ChannelListContext)
  const { enqueueSnackbar } = useSnackbar()
  const [openEditNameModal, setOpenEditNameModal] = useState(false)
  const [channelName, setChannelName] = useState(channel?.data.name)
  const [isMuted, setIsMuted] = useState(() => channel.muteStatus().muted)
  const [muteDialogOpen, setMuteDialogOpen] = useState(false)
  const isHidden = Boolean(channel?.data?.hidden || channel?.hidden)
  const promotions = usePromotions()

  useEffect(() => {
    const handler = () => setIsMuted(channel.muteStatus().muted)
    client?.on('notification.channel_mutes_updated', handler)
    return () => client?.off('notification.channel_mutes_updated', handler)
  }, [channel, client])

  const isChatOwner = channel?.data?.created_by?.id === user?.externalId
  const isGroupConvo = isGroupChannel(channel)
  const showAddMembers =
    isGroupConvo && !isDistinctChannel(channel) && isChatOwner

  const filters = {
    members: { $in: [client?.user?.id] },
    frozen: false,
    hidden: false,
  }
  const sort = { last_message_at: -1 }

  const redirect = async () => {
    const [ch] = await client.queryChannels(filters, sort, { limit: 1 })
    if (ch != null) {
      setActiveChannel(ch)
    }
  }

  const handleUnmute = async () => {
    await channel.unmute()
    enqueueSnackbar('Notifications unmuted', { variant: 'success' })
  }

  const handleUnhide = async () => {
    await channel.show()
    setListTab('inbox')
    setActiveChannel(channel)
    enqueueSnackbar('Conversation unhidden', { variant: 'success' })
  }

  return (
    <Fragment>
      <ActionsMenu
        ariaId="conversation-actions"
        buttonContent={<MoreHorizIcon />}
        className={classes.overflowButton}
        color="default"
        variant="outlined"
      >
        {promotions && (
          <MenuItem key="promotions" onClick={promotions.openSheet}>
            <ListItemIcon className={classes.menuIcon}>
              <VolumeUpIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Promotions" />
            <Typography variant="body2" color="textSecondary">
              {promotions.enabled ? 'On' : 'Off'}
            </Typography>
          </MenuItem>
        )}
        {isHidden ? (
          <MenuItem onClick={handleUnhide}>
            <ListItemIcon className={classes.menuIcon}>
              <VisibilityOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Unhide Conversation" />
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() =>
              dispatch({ type: 'hide', onSuccess: () => channel.hide() })
            }
          >
            <ListItemIcon className={classes.menuIcon}>
              <VisibilityOffOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Hide Conversation" />
          </MenuItem>
        )}
        {isMuted ? (
          <MenuItem onClick={handleUnmute}>
            <ListItemIcon className={classes.menuIcon}>
              <NotificationsActiveOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Unmute Notifications" />
          </MenuItem>
        ) : (
          <MenuItem onClick={() => setMuteDialogOpen(true)}>
            <ListItemIcon className={classes.menuIcon}>
              <NotificationsOffOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Mute Notifications" />
          </MenuItem>
        )}
        {isGroupConvo
          ? [
            isChatOwner && (
              <MenuItem
                key="edit-name"
                onClick={() => setOpenEditNameModal(true)}
              >
                <ListItemIcon className={classes.menuIcon}>
                  <EditOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Edit Group Name" />
              </MenuItem>
            ),
            <MenuItem
              key="leave"
              onClick={() =>
                dispatch({
                  type: 'leave',
                  onSuccess: () => channel.removeMembers([user?.externalId]),
                })
              }
            >
              <ListItemIcon className={classes.menuIcon}>
                <LeaveGroupConversation fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Leave Group" />
            </MenuItem>,
          ].filter(Boolean)
          : null}
        {showAddMembers && (
          <AddMembersDialog
            trigger={
              <MenuItem>
                <ListItemIcon className={classes.menuIcon}>
                  <GroupAddOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Add Members" />
              </MenuItem>
            }
            channel={channel}
          />
        )}
      </ActionsMenu>
      <ConfirmationDialog
        open={state.isConfirmationOpen}
        onSuccess={async () => {
          const action = state.successMessage
          await state.onSuccess()
          enqueueSnackbar(action, { variant: 'success' })
          await redirect()
        }}
        setOpen={() => dispatch({ type: 'close', value: null })}
        title={state.text}
        actionText={state.actionText}
        content={state.content}
      />
      <MuteChannelDialog
        open={muteDialogOpen}
        setOpen={setMuteDialogOpen}
        channel={channel}
        onSuccess={() =>
          enqueueSnackbar('Notifications muted', { variant: 'success' })
        }
      />
      <EditGroupNameModal
        open={openEditNameModal}
        setOpen={setOpenEditNameModal}
        channelName={channelName}
        setChannelName={setChannelName}
        channel={channel}
      />
    </Fragment>
  )
}

function ConfirmationDialog({
  open,
  onSuccess,
  setOpen,
  title,
  content,
  actionText,
}) {
  const classes = useConfirmationDialogStyles()

  return (
    <Dialog fullWidth maxWidth="xs" onClose={() => setOpen()} open={open}>
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <Typography className={classes.title}>{title}</Typography>
        <IconButton
          aria-label="Close"
          className={classes.closeButton}
          onClick={() => setOpen()}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>{content}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={() => setOpen()}>
          Cancel
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={async () => await onSuccess()}
        >
          {actionText}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ConversationActionsMenu
